import { defineStore } from 'pinia'
import { invoke } from '~/lib/tauriInvoke'
import * as Sentry from '@sentry/nuxt'
import { runAction } from '~/stores/storeErrorHandling'
import {
  FriendshipStatus,
  type BlockedUser,
  type Friend,
  type FriendRequest,
  type FriendSignalREventPayload,
  type FriendshipStatusResponse,
  type GetBlockedUsersResponse,
  type GetFriendsResponse,
  type GetPendingRequestsResponse,
} from '~/types/friends'

export type { Friend, FriendRequest, BlockedUser } from '~/types/friends'
export { FriendshipStatus } from '~/types/friends'

// Matches the page size used for message/group list pagination elsewhere in the app.
export const FRIENDS_PAGE_LIMIT = 50

export const useFriendsStore = defineStore('friends', {
  state: () => ({
    friends: [] as Friend[],
    incomingRequests: [] as FriendRequest[],
    outgoingRequests: [] as FriendRequest[],
    blockedUsers: [] as BlockedUser[],
    hasMoreFriends: false,
    hasMoreIncoming: false,
    hasMoreOutgoing: false,
    hasMoreBlocked: false,
    isLoading: false,
    /** The three lists have been fetched at least once this session — after that they're shown as
     * they are and kept current by the users SignalR hub (and a full refetch on reconnect). */
    loaded: false,
    error: null as string | null,
  }),
  actions: {
    // Primary panel load — the three lists are independent gRPC calls, so fire them in parallel
    // rather than the caller awaiting them one at a time.
    async loadAll() {
      this.isLoading = true
      try {
        await Promise.all([this.fetchFriends(), this.fetchIncomingRequests(), this.fetchOutgoingRequests()])
        this.loaded = true
      } finally {
        this.isLoading = false
      }
    },

    // Cache-first, like a group's members and roles: the first screen that needs the lists loads
    // them; every later visit (back to the home page, the friends tab) shows what's there at once.
    ensureLoaded() {
      if (this.loaded || this.isLoading) return
      void this.loadAll()
    },

    async fetchFriends(beforeId?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'fetchFriends', extra: { beforeId } },
        async () => {
          const response = await invoke<GetFriendsResponse>('get_friends', { limit: FRIENDS_PAGE_LIMIT, beforeId })
          if (beforeId) {
            const existingIds = new Set(this.friends.map((f) => f.id))
            this.friends.push(...response.friends.filter((f) => !existingIds.has(f.id)))
          } else {
            this.friends = response.friends
          }
          this.hasMoreFriends = response.has_more
          return response
        },
      )
    },

    async fetchIncomingRequests(beforeId?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'fetchIncomingRequests', extra: { beforeId } },
        async () => {
          const response = await invoke<GetPendingRequestsResponse>('get_incoming_requests', { limit: FRIENDS_PAGE_LIMIT, beforeId })
          if (beforeId) {
            const existingIds = new Set(this.incomingRequests.map((r) => r.id))
            this.incomingRequests.push(...response.requests.filter((r) => !existingIds.has(r.id)))
          } else {
            this.incomingRequests = response.requests
          }
          this.hasMoreIncoming = response.has_more
          return response
        },
      )
    },

    async fetchOutgoingRequests(beforeId?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'fetchOutgoingRequests', extra: { beforeId } },
        async () => {
          const response = await invoke<GetPendingRequestsResponse>('get_outgoing_requests', { limit: FRIENDS_PAGE_LIMIT, beforeId })
          if (beforeId) {
            const existingIds = new Set(this.outgoingRequests.map((r) => r.id))
            this.outgoingRequests.push(...response.requests.filter((r) => !existingIds.has(r.id)))
          } else {
            this.outgoingRequests = response.requests
          }
          this.hasMoreOutgoing = response.has_more
          return response
        },
      )
    },

    async fetchBlockedUsers(beforeId?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'fetchBlockedUsers', extra: { beforeId } },
        async () => {
          const response = await invoke<GetBlockedUsersResponse>('get_blocked_users', { limit: FRIENDS_PAGE_LIMIT, beforeId })
          if (beforeId) {
            const existingIds = new Set(this.blockedUsers.map((u) => u.user_id))
            this.blockedUsers.push(...response.users.filter((u) => !existingIds.has(u.user_id)))
          } else {
            this.blockedUsers = response.users
          }
          this.hasMoreBlocked = response.has_more
          return response
        },
      )
    },

    // Returns the created/updated request so the caller (the add-friend dialog) can special-case
    // an immediate Accepted result (the other side already had an outgoing request waiting).
    async sendFriendRequest(username: string, message?: string) {
      const request = await invoke<FriendRequest>('send_friend_request', { payload: { username, message } })
      if (request.status === FriendshipStatus.Accepted) {
        this.upsertFriend({
          id: request.id,
          other_user_id: request.other_user_id,
          other_username: request.other_username,
          other_display_name: request.other_display_name,
          other_avatar_url: request.other_avatar_url,
          status: FriendshipStatus.Accepted,
          confirmed_at: new Date().toISOString(),
        })
        this.removeOutgoingRequest(request.id)
      } else {
        this.upsertOutgoingRequest(request)
      }
      return request
    },

    async acceptFriendRequest(otherUserId: string) {
      const previous = this.incomingRequests
      this.incomingRequests = previous.filter((r) => r.other_user_id !== otherUserId)
      try {
        const friend = await invoke<Friend>('accept_friend_request', { friendId: otherUserId })
        this.upsertFriend(friend)
        return friend
      } catch (e: any) {
        this.incomingRequests = previous
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'acceptFriendRequest', otherUserId } })
        throw e
      }
    },

    async declineFriendRequest(otherUserId: string) {
      const previous = this.incomingRequests
      this.incomingRequests = previous.filter((r) => r.other_user_id !== otherUserId)
      try {
        await invoke('decline_friend_request', { friendId: otherUserId })
      } catch (e: any) {
        this.incomingRequests = previous
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'declineFriendRequest', otherUserId } })
        throw e
      }
    },

    async cancelFriendRequest(otherUserId: string) {
      const previous = this.outgoingRequests
      this.outgoingRequests = previous.filter((r) => r.other_user_id !== otherUserId)
      try {
        await invoke('cancel_friend_request', { friendId: otherUserId })
      } catch (e: any) {
        this.outgoingRequests = previous
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'cancelFriendRequest', otherUserId } })
        throw e
      }
    },

    async removeFriend(otherUserId: string) {
      const previous = this.friends
      this.friends = previous.filter((f) => f.other_user_id !== otherUserId)
      try {
        await invoke('remove_friend', { friendId: otherUserId })
      } catch (e: any) {
        this.friends = previous
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'removeFriend', otherUserId } })
        throw e
      }
    },

    async blockUser(username: string, reason?: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'blockUser', extra: { username } },
        async () => {
          await invoke('block_user', { payload: { username, reason } })
          // The person being blocked might currently be a friend or a pending request on either
          // side — strip them out of every list locally rather than doing a full refetch.
          this.friends = this.friends.filter((f) => f.other_username !== username)
          this.incomingRequests = this.incomingRequests.filter((r) => r.other_username !== username)
          this.outgoingRequests = this.outgoingRequests.filter((r) => r.other_username !== username)
        },
      )
    },

    async unblockUser(userId: string) {
      const previous = this.blockedUsers
      this.blockedUsers = previous.filter((u) => u.user_id !== userId)
      try {
        await invoke('unblock_user', { userId })
      } catch (e: any) {
        this.blockedUsers = previous
        this.error = e?.message || String(e)
        Sentry.captureException(e, { extra: { action: 'unblockUser', userId } })
        throw e
      }
    },

    async getFriendshipStatus(userId: string) {
      return runAction(
        (msg) => (this.error = msg),
        { action: 'getFriendshipStatus', extra: { userId } },
        () => invoke<FriendshipStatusResponse>('get_friendship_status', { userId }),
      )
    },

    upsertFriend(friend: Friend) {
      const index = this.friends.findIndex((f) => f.id === friend.id)
      if (index !== -1) {
        this.friends[index] = friend
      } else {
        this.friends.push(friend)
      }
    },

    upsertIncomingRequest(request: FriendRequest) {
      const index = this.incomingRequests.findIndex((r) => r.id === request.id)
      if (index !== -1) {
        this.incomingRequests[index] = request
      } else {
        this.incomingRequests.push(request)
      }
    },

    upsertOutgoingRequest(request: FriendRequest) {
      const index = this.outgoingRequests.findIndex((r) => r.id === request.id)
      if (index !== -1) {
        this.outgoingRequests[index] = request
      } else {
        this.outgoingRequests.push(request)
      }
    },

    removeIncomingRequest(friendId: string) {
      this.incomingRequests = this.incomingRequests.filter((r) => r.id !== friendId)
    },

    removeOutgoingRequest(friendId: string) {
      this.outgoingRequests = this.outgoingRequests.filter((r) => r.id !== friendId)
    },

    removeFriendLocal(friendId: string) {
      this.friends = this.friends.filter((f) => f.id !== friendId)
    },

    // Central entry point for every `user-events` friend payload — id-keyed upsert per the
    // real-time sync rules (dedupe by `friendId`, never blind-push), and additionally used after
    // a SignalR reconnect to trigger a full list refetch rather than trusting a buffered replay.
    applyRealtimeEvent(type: string, payload: FriendSignalREventPayload) {
      const status = payload.status
      const request: FriendRequest = {
        id: payload.friendId,
        other_user_id: payload.otherUserId,
        other_username: payload.otherUsername,
        other_display_name: payload.otherDisplayName ?? '',
        other_avatar_url: payload.otherAvatarUrl,
        status,
        message: payload.message ?? '',
        created_at: payload.timestamp,
      }

      switch (type) {
        case 'FriendRequestCreated':
          this.upsertIncomingRequest(request)
          break
        case 'FriendRequestSent':
          // Multi-device sync: this device didn't necessarily originate the request itself.
          this.upsertOutgoingRequest(request)
          break
        case 'FriendRequestAccepted':
          this.removeIncomingRequest(payload.friendId)
          this.removeOutgoingRequest(payload.friendId)
          this.upsertFriend({
            id: payload.friendId,
            other_user_id: payload.otherUserId,
            other_username: payload.otherUsername,
            other_display_name: payload.otherDisplayName ?? '',
            other_avatar_url: payload.otherAvatarUrl,
            status: FriendshipStatus.Accepted,
            confirmed_at: payload.timestamp,
          })
          break
        case 'FriendRequestDeclined':
          this.removeOutgoingRequest(payload.friendId)
          break
        case 'FriendRequestCancelled':
          this.removeIncomingRequest(payload.friendId)
          break
        case 'FriendRemoved':
          // Multi-device sync: can also be an echo of this device's own removeFriend() action.
          this.removeFriendLocal(payload.friendId)
          break
        case 'FriendUserBlocked':
          this.friends = this.friends.filter((f) => f.other_user_id !== payload.otherUserId)
          this.incomingRequests = this.incomingRequests.filter((r) => r.other_user_id !== payload.otherUserId)
          this.outgoingRequests = this.outgoingRequests.filter((r) => r.other_user_id !== payload.otherUserId)
          break
      }
    },
  },
})
